import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm, NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

// libraries
import { CookieService } from 'ngx-cookie-service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { switchMap } from 'rxjs/operators';

//Services
import {
  CHANNELS_ENUMERATION,
  CHANNELS_EVENTS_ENUMERATION,
  CustomerRecurrenceService,
  MarketingEventService,
} from '@shared/services';
import { IpResolverService } from '@commons/services/ip-resolver.service';
import { LoginException } from '@account/exceptions/login-exception';
import { retryLoginWithDelay } from '@core/operators/retry-delay';
import { ValidatorService } from '@shared/utility/validator.service';
import { AuthService } from '@auth/auth.service';
import { AuthCasesService, CreateAccountCaseService, LoginMessagesService } from './services';

// models
import { User } from '@model/user';

// envs
import { environment } from '@environments/environment';

// Shared
import { TITLE_ERROR, show_popup } from '@shared/utility/popup-error';
import { ModalErrorComponent } from '@shared/components/modal-error/modal-error.component';
import { ERRORS_CONSTANTS } from './error.constants';
import { COOKIE_ENUMERATION } from './configs';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  user: User;
  hide: boolean = true;
  showLoader: boolean = false;
  response: string;
  URL_OLVIDO_PASSWORD: string = `${environment.clientRapiplazo}login`;
  ipAddress: string;
  private domain: string = environment.domain;
  @ViewChild('clientForm') public clientForm: NgForm;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    public readonly validatorService: ValidatorService,
    private readonly cookieService: CookieService,
    private readonly recaptchaV3Service: ReCaptchaV3Service,
    private readonly activateRoute: ActivatedRoute,
    private readonly ipService: IpResolverService,
    private readonly statusService: CustomerRecurrenceService,
    private readonly authCasesService: AuthCasesService,
    private readonly createAccountCaseService: CreateAccountCaseService,
    private readonly dialog: MatDialog,
    private readonly loginMessagesService: LoginMessagesService,
    private readonly marketingEventService: MarketingEventService
  ) {
    this.user = new User();
  }

  public ngOnInit(): void {
    this.initializeSession();
  }

  private initializeSession(): void {
    if (this.hasLoggedOutFromMyCredits()) {
      this.authService.logout();
      return;
    }
    this.associateExistingUser();
    this.getIpAddress();
    this.queryParams();
  }

  private hasLoggedOutFromMyCredits(): boolean {
    const cookie = this.cookieService.get(COOKIE_ENUMERATION.MY_CREDITS_LOGOUT);
    return !!cookie;
  }

  private associateExistingUser(): void {
    const email: string = sessionStorage.getItem('email');
    if (email) {
      this.user.username = email;
    }
  }

  public queryParams(): void {
    const { isNewLoan, error } = this.activateRoute.snapshot.queryParams;
    const newLoan = Boolean(isNewLoan) || Boolean(sessionStorage.getItem('ac'));
    this.validateMinimumConditions(newLoan);
    if (!this._processPostLoginRedirection() && this.authService.isAuthenticated()) {
      this.redirectIfANewRequest();
    }
    if (error) {
      this.showErrorModal(error);
    }
    this.marketingEventService.notifyEventToChannel(CHANNELS_ENUMERATION.CLOSE_CHANNEL, {
      event: CHANNELS_EVENTS_ENUMERATION.LOGIN,
    });
  }

  private redirectIfANewRequest() {
    if (this.user.nuevaSolicitud) {
      this.authService.logout();
      this.router.navigate(['/login'], { queryParams: { isNewLoan: true } });
    } else {
      show_popup('Login', `Hola ${this.authService.user.username}, ya estas autenticado!`);
      this.router.navigate(['/home']);
    }
  }

  public showErrorModal(errorCode: string): void {
    const sanitizedErrorCode = errorCode.replace(/-/g, '');
    const error = ERRORS_CONSTANTS.errors[sanitizedErrorCode];
    let dialogData = {
      text: error?.text || 'Error, por favor intenta nuevamente.',
      description: error?.description || null,
      phone: error?.phone || null,
      code: error?.code || null,
    };
    this.dialog.open(ModalErrorComponent, {
      data: dialogData,
      panelClass: 'custom-dialog-container',
    });
  }

  private validateMinimumConditions(newRequest: boolean): void {
    if (newRequest) {
      this.handleNewRequest();
    } else {
      this.setNewLoanStatus(false);
    }
  }

  private handleNewRequest(): void {
    const amount = this.getLoanAmount();
    const term = this.getLoanTerm();

    if (!amount || !term) {
      this.setNewLoanStatus(false);
    } else {
      this.setNewLoanStatus(true);
    }
  }

  private getLoanAmount(): string | null {
    return this.cookieService.get('amount') || sessionStorage.getItem('amount');
  }

  private getLoanTerm(): string | null {
    return this.cookieService.get('term') || sessionStorage.getItem('term');
  }

  private setNewLoanStatus(isNewLoan: boolean): void {
    this.cookieService.set('isNewLoan', JSON.stringify(isNewLoan), 1, '/', this.domain);
    this.user.nuevaSolicitud = isNewLoan;
  }

  private _processPostLoginRedirection(): boolean {
    const item = sessionStorage.getItem('flow');
    return item && item == 'origination_process' ? true : false;
  }

  public submit(): void {
    if (this.clientForm.form.valid) {
      this.login();
    } else {
      this.clientForm.form.markAllAsTouched();
    }
  }

  public createAccount() {
    this.createAccountCaseService.execute();
  }

  private login(): void {
    this.showLoader = true;

    this.user.recaptchaOrigin = 'LOGIN_PAGE';
    this.user.amount = this.cookieService.get('amount') || sessionStorage.getItem('amount');
    this.user.term = this.cookieService.get('term') || sessionStorage.getItem('term');

    this.recaptchaV3Service
      .execute('login_page')
      .pipe(
        switchMap((token) => {
          this.user.recaptchaToken = token;
          return this.authService.login(this.user);
        }),
        this.authService.verifyLoginResponse(),
        this.statusService.combineAuthenticationAndRecurrenceTypeResponses(),
        retryLoginWithDelay(5000, 0)
      )
      .subscribe({
        next: this.handleLoginSuccess.bind(this),
        error: this.handleLoginError.bind(this),
      });
  }

  private handleLoginSuccess({ loginResponse, recurrenceResponse }): void {
    this.authCasesService.execute({
      loginResponse,
      recurrenceResponse,
      newRequest: this.user.nuevaSolicitud,
    });
  }

  private handleLoginError(error: Error & LoginException): void {
    this.clientForm.form.controls['passwordLogin'].setValue('');
    this.clientForm.form.controls['passwordLogin'].markAsPristine();
    this.clientForm.form.controls['passwordLogin'].setErrors(null);
    if (error.name === LoginException.ERROR_NAME) {
      this.showLoader = false;
      this.loginMessagesService.showPopup(error.code, error.message);
    } else {
      show_popup(TITLE_ERROR, 'Lo sentimos. Ocurrió un error al iniciar sesión. Inténtalo más tarde.');
    }
    this.showLoader = false;
  }

  public getErrorMessageEmail(usernameLogin: NgModel): string {
    return this.validatorService.getErrorMessageEmail(usernameLogin);
  }

  public getErrorMessagePassword(passwordLogin: NgModel): string {
    return this.validatorService.getErrorMessagePassword(passwordLogin);
  }

  public setCookieAndRedirect(): void {
    const now = new Date();
    now.setHours(now.getHours() + 24);
    this.cookieService.set('urlRedirect', this.URL_OLVIDO_PASSWORD, now, '/', this.domain, true, 'Lax');
    this.router.navigate(['changePassword']);
  }

  public getIpAddress(): void {
    this.ipService.getIPAddress().subscribe((data) => {
      this.ipAddress = data.response;
    });
  }

  public redirectToPayment(): void {
    window.open(environment.sessionlessPaymentUrl, '_blank');
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      setTimeout(() => {
        this.submit();
      }, 200);
    }
  }
}
